import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FileText, Video, Image as ImageIcon, Search, ExternalLink, Download, FolderOpen, ChevronRight, ArrowLeft, Cloud, Database } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const typeIcons = { pdf: FileText, video: Video, image: ImageIcon, folder: FolderOpen };
const typeLabels = { pdf: 'PDF', video: 'Video', image: 'Şəkil', document: 'Sənəd' };

function getFileType(name) {
  if (!name) return 'document';
  const ext = name.split('.').pop().toLowerCase();
  if (['pdf'].includes(ext)) return 'pdf';
  if (['mp4', 'avi', 'mov', 'mkv', 'webm'].includes(ext)) return 'video';
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(ext)) return 'image';
  if (['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt'].includes(ext)) return 'document';
  return 'document';
}

export default function Resources() {
  const [resources, setResources] = useState([]);
  const [spFiles, setSpFiles] = useState([]);
  const [spFolder, setSpFolder] = useState('Sayt/Resurslar');
  const [spBreadcrumb, setSpBreadcrumb] = useState([{ name: 'Resurslar', path: 'Sayt/Resurslar' }]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeSource, setActiveSource] = useState('all');
  const [activeType, setActiveType] = useState('all');
  const [spConnected, setSpConnected] = useState(false);

  useEffect(() => {
    fetchResources();
    checkSharePoint();
  }, []);

  useEffect(() => {
    fetchSpFiles();
  }, [spFolder]);

  const fetchResources = async () => {
    try {
      const { data } = await axios.get(`${API}/api/resources`);
      setResources(data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const checkSharePoint = async () => {
    try {
      const { data } = await axios.get(`${API}/api/sharepoint/status`);
      setSpConnected(data.connected);
    } catch { setSpConnected(false); }
  };

  const fetchSpFiles = async () => {
    try {
      const { data } = await axios.get(`${API}/api/sharepoint/files?folder=${encodeURIComponent(spFolder)}`);
      setSpFiles(data);
    } catch (err) { console.error(err); setSpFiles([]); }
  };

  const navigateFolder = (folder) => {
    const newPath = `${spFolder}/${folder.name}`;
    setSpFolder(newPath);
    setSpBreadcrumb(prev => [...prev, { name: folder.name, path: newPath }]);
  };

  const navigateBreadcrumb = (index) => {
    const target = spBreadcrumb[index];
    setSpFolder(target.path);
    setSpBreadcrumb(prev => prev.slice(0, index + 1));
  };

  const downloadSpFile = async (fileId) => {
    try {
      const { data } = await axios.get(`${API}/api/sharepoint/download/${fileId}`);
      if (data.download_url) window.open(data.download_url, '_blank');
    } catch (err) { console.error(err); }
  };

  const getResourceUrl = (r) => {
    if (r.url && (r.url.startsWith('http://') || r.url.startsWith('https://'))) return r.url;
    if (r.url) return `${API}/api/files/${r.url}`;
    return '#';
  };

  // Combined items
  const filteredResources = resources.filter(r => {
    const matchType = activeType === 'all' || r.resource_type === activeType;
    const matchSearch = r.title.toLowerCase().includes(search.toLowerCase()) || (r.description || '').toLowerCase().includes(search.toLowerCase());
    return matchType && matchSearch;
  });

  const filteredSpFiles = spFiles.filter(f => {
    if (f.type === 'folder') return true;
    const ft = getFileType(f.name);
    const matchType = activeType === 'all' || ft === activeType;
    const matchSearch = f.name.toLowerCase().includes(search.toLowerCase());
    return matchType && matchSearch;
  });

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#F4F7F6]"><div className="w-10 h-10 border-4 border-[#0077B6] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div>
      {/* Hero */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img src={`${API}/api/files/dersimizcografiya/uploads/dc916a59-e06e-4d0c-b486-c8cf7b3683eb.jpg`} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#03045E]/85" />
        </div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <span className="inline-block px-4 py-2 rounded-full bg-[#0077B6]/20 text-[#48CAE4] text-sm font-medium mb-4">Resurslar</span>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mt-4 tracking-tight">
              Dərs Vəsaitləri
            </h1>
            <p className="font-body text-white/70 text-base md:text-lg max-w-2xl mx-auto mt-6">
              PDF materiallar, video dərslər, sənədlər və əyani vəsaitlər
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 md:py-24 bg-[#F4F7F6]" data-testid="resources-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* Search & Filters */}
          <div className="flex flex-col md:flex-row gap-4 mb-8">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#5C6B7A]" />
              <input data-testid="resources-search" type="text" placeholder="Resurs axtar..." value={search} onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-12 pr-4 py-3 rounded-xl bg-white border border-[#E2E8F0] text-[#1A2530] placeholder:text-[#5C6B7A]/50 focus:outline-none focus:ring-2 focus:ring-[#0077B6]/20 font-body text-sm" />
            </div>
            <div className="flex gap-2 flex-wrap">
              <Tabs value={activeType} onValueChange={setActiveType}>
                <TabsList className="bg-white border border-[#E2E8F0] h-12">
                  <TabsTrigger value="all" className="data-[state=active]:bg-[#0077B6] data-[state=active]:text-white font-body">Hamısı</TabsTrigger>
                  <TabsTrigger value="pdf" className="data-[state=active]:bg-[#0077B6] data-[state=active]:text-white font-body">PDF</TabsTrigger>
                  <TabsTrigger value="video" className="data-[state=active]:bg-[#0077B6] data-[state=active]:text-white font-body">Video</TabsTrigger>
                  <TabsTrigger value="image" className="data-[state=active]:bg-[#0077B6] data-[state=active]:text-white font-body">Şəkil</TabsTrigger>
                  <TabsTrigger value="document" className="data-[state=active]:bg-[#0077B6] data-[state=active]:text-white font-body">Sənəd</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          </div>

          {/* SharePoint Files Section */}
          {spConnected && (
            <div className="mb-12" data-testid="sharepoint-section">
              <div className="flex items-center gap-3 mb-4">
                <Cloud className="w-5 h-5 text-[#0077B6]" />
                <h2 className="font-heading text-2xl text-[#1A2530]">SharePoint Faylları</h2>
                <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs font-semibold font-body">Qoşulu</span>
              </div>

              {/* Breadcrumb */}
              <div className="flex items-center gap-1 mb-4 text-sm font-body">
                {spBreadcrumb.map((b, i) => (
                  <React.Fragment key={i}>
                    {i > 0 && <ChevronRight className="w-3.5 h-3.5 text-[#5C6B7A]" />}
                    <button onClick={() => navigateBreadcrumb(i)}
                      className={`px-2 py-1 rounded-md transition-colors ${i === spBreadcrumb.length - 1 ? 'text-[#0077B6] font-semibold' : 'text-[#5C6B7A] hover:text-[#1A2530]'}`}>
                      {b.name}
                    </button>
                  </React.Fragment>
                ))}
              </div>

              {/* SP Files Grid */}
              {filteredSpFiles.length === 0 ? (
                <div className="bg-white rounded-xl border border-[#E2E8F0] p-8 text-center">
                  <FolderOpen className="w-12 h-12 text-[#E2E8F0] mx-auto mb-3" />
                  <p className="font-body text-[#5C6B7A]">Bu qovluq boşdur</p>
                  <p className="font-body text-xs text-[#5C6B7A]/60 mt-1">Admin paneldən SharePoint-ə fayl yükləyə bilərsiniz</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {filteredSpFiles.map((file, i) => {
                    const isFolder = file.type === 'folder';
                    const fType = isFolder ? 'folder' : getFileType(file.name);
                    const Icon = typeIcons[fType] || FileText;

                    return (
                      <motion.div key={file.id} className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden card-hover group cursor-pointer"
                        initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.03 }}
                        onClick={() => isFolder ? navigateFolder(file) : downloadSpFile(file.id)} data-testid={`sp-file-${file.id}`}>
                        <div className="p-5">
                          <div className="flex items-start gap-3">
                            <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${isFolder ? 'bg-amber-50' : 'bg-[#0077B6]/10'}`}>
                              <Icon className={`w-5 h-5 ${isFolder ? 'text-amber-500' : 'text-[#0077B6]'}`} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="font-body font-semibold text-[#1A2530] text-sm truncate">{file.name}</h4>
                              <div className="flex items-center gap-2 mt-1">
                                {isFolder ? (
                                  <span className="font-body text-xs text-amber-600">Qovluq</span>
                                ) : (
                                  <>
                                    <span className="font-body text-xs text-[#0077B6] font-semibold uppercase">{file.name.split('.').pop()}</span>
                                    {file.size > 0 && <span className="font-body text-xs text-[#5C6B7A]">{(file.size / 1024).toFixed(0)} KB</span>}
                                  </>
                                )}
                              </div>
                            </div>
                            {isFolder ? (
                              <ChevronRight className="w-4 h-4 text-[#5C6B7A] group-hover:text-[#0077B6] transition-colors" />
                            ) : (
                              <Download className="w-4 h-4 text-[#5C6B7A] group-hover:text-[#0077B6] transition-colors" />
                            )}
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* DB Resources Section */}
          <div data-testid="db-resources-section">
            {(spConnected && filteredResources.length > 0) && (
              <div className="flex items-center gap-3 mb-4">
                <Database className="w-5 h-5 text-[#0077B6]" />
                <h2 className="font-heading text-2xl text-[#1A2530]">Əlavə Resurslar</h2>
              </div>
            )}

            {loading ? (
              <div className="text-center py-20">
                <div className="w-10 h-10 border-4 border-[#0077B6] border-t-transparent rounded-full animate-spin mx-auto" />
              </div>
            ) : filteredResources.length === 0 && !spConnected ? (
              <div className="text-center py-20">
                <FileText className="w-16 h-16 text-[#E2E8F0] mx-auto mb-4" />
                <p className="font-body text-[#5C6B7A] text-lg">Hələ ki resurs yoxdur</p>
                <p className="font-body text-[#5C6B7A]/60 text-sm mt-2">Yeni resurslar tezliklə əlavə olunacaq</p>
              </div>
            ) : filteredResources.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredResources.map((resource, i) => {
                  const Icon = typeIcons[resource.resource_type] || FileText;
                  const url = getResourceUrl(resource);
                  const isYoutube = url.includes('youtube.com') || url.includes('youtu.be');

                  return (
                    <motion.div key={resource.id} className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden card-hover group"
                      initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
                      data-testid={`resource-card-${resource.id}`}>
                      {resource.resource_type === 'video' && isYoutube && (
                        <div className="aspect-video bg-[#1A2530]">
                          <iframe src={url.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/')} className="w-full h-full" allowFullScreen title={resource.title} />
                        </div>
                      )}
                      {resource.resource_type === 'image' && (
                        <div className="aspect-video bg-[#F4F7F6]">
                          <img src={url} alt={resource.title} className="w-full h-full object-cover" />
                        </div>
                      )}
                      <div className="p-6">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-10 h-10 rounded-lg bg-[#0077B6]/10 flex items-center justify-center">
                            <Icon className="w-5 h-5 text-[#0077B6]" />
                          </div>
                          <span className="text-xs font-bold tracking-wider uppercase text-[#0077B6] font-body">
                            {typeLabels[resource.resource_type] || resource.resource_type}
                          </span>
                        </div>
                        <h3 className="text-lg font-semibold text-[#1A2530] mb-2 line-clamp-2">{resource.title}</h3>
                        {resource.description && <p className="font-body text-sm text-[#5C6B7A] mb-4 line-clamp-2">{resource.description}</p>}
                        <a href={url} target="_blank" rel="noopener noreferrer" data-testid={`resource-link-${resource.id}`}
                          className="inline-flex items-center gap-2 text-sm font-semibold text-[#0077B6] hover:text-[#005f92] transition-colors font-body">
                          {resource.resource_type === 'pdf' ? <><Download className="w-4 h-4" /> Yüklə</> : <><ExternalLink className="w-4 h-4" /> Aç</>}
                        </a>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            ) : null}
          </div>
        </div>
      </section>
    </div>
  );
}
